import React, { useState, useEffect, useCallback } from 'react'
import { Card, Form, Button, ListGroup, Badge, Row, Col, Container, InputGroup } from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabase'
import Layout from '../components/Layout'
import ProtectedRoute from '../components/ProtectedRoute'

function CheckinCoachName() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [coaches, setCoaches] = useState([])
  const [filteredCoaches, setFilteredCoaches] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [session, setSession] = useState(null)
  const [presences, setPresences] = useState([])
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUser(user)
    }
    getUser()
  }, [])

  const fetchSession = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('*, course_types (name, location)')
        .eq('id', sessionId)
        .single()
      if (error) throw error
      setSession(data)
    } catch (error) {
      console.error('Error fetching session:', error)
    }
  }, [sessionId])

  const fetchCoaches = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('coaches')
        .select('*')
        .order('last_name')
      if (error) throw error
      setCoaches(data || [])
      setFilteredCoaches(data || [])
    } catch (error) {
      console.error('Error fetching coaches:', error)
    }
  }, [])

  const fetchPresences = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('presences')
        .select('*')
        .eq('session_id', sessionId)
        .eq('role', 'coach')           // ✅ filtre sur role = 'coach'
      if (error) throw error
      setPresences(data || [])
    } catch (error) {
      console.error('Error fetching presences:', error)
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    const loadData = async () => {
      await fetchSession()
      await fetchCoaches()
      await fetchPresences()
    }
    loadData()
  }, [fetchSession, fetchCoaches, fetchPresences])

  const handleSearch = (term) => {
    setSearchTerm(term)
    if (!term) {
      setFilteredCoaches(coaches)
    } else {
      setFilteredCoaches(coaches.filter(c =>
        c.first_name.toLowerCase().includes(term.toLowerCase()) ||
        c.last_name.toLowerCase().includes(term.toLowerCase()) ||
        (c.sports || []).some(s => s.toLowerCase().includes(term.toLowerCase()))
      ))
    }
  }

  const markPresence = async (coachId) => {
    try {
      const isAlreadyPresent = presences.some(p => p.coach_id === coachId)
      if (isAlreadyPresent) {
        alert('Ce coach est déjà marqué présent')
        return
      }
      const { error } = await supabase
        .from('presences')
        .insert([{
          coach_id: coachId,           // ✅ coach_id (pas participant_id)
          participant_id: null,        // ✅ null car c'est un coach
          session_id: sessionId,
          mode: 'name',
          role: 'coach'               // ✅ role = 'coach'
        }])
      if (error) throw error
      await fetchPresences()
    } catch (error) {
      console.error('Error marking presence:', error)
      alert('Erreur lors du marquage de la présence: ' + error.message)
    }
  }

  const isPresent = (coachId) => presences.some(p => p.coach_id === coachId)

  if (loading) return (
    <ProtectedRoute><Layout user={user}>
      <Container className="text-center py-5">
        <div className="spinner-border text-warning" role="status"><span className="visually-hidden">Chargement...</span></div>
        <p className="mt-3">Chargement...</p>
      </Container>
    </Layout></ProtectedRoute>
  )

  if (!session) return (
    <ProtectedRoute><Layout user={user}>
      <Container className="text-center py-5">
        <h4 className="text-danger">Session non trouvée</h4>
        <Button variant="primary" onClick={() => navigate('/')}>Retour au Dashboard</Button>
      </Container>
    </Layout></ProtectedRoute>
  )

  return (
    <ProtectedRoute>
      <Layout user={user}>
        <Container fluid="lg" className="py-4">

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <Button variant="outline-secondary" onClick={() => navigate('/')} className="me-3" size="lg">◀ Retour</Button>
              <div>
                <h2 className="h3 mb-1 fw-bold">🏅 Signature Coach — Par Nom</h2>
                <p className="text-muted mb-0">
                  📅 {new Date(session.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  <span className="mx-2">•</span>🏷️ {session.course_types?.name}
                  <span className="mx-2">•</span>📍 {session.course_types?.location}
                </p>
              </div>
            </div>
            <Badge bg="warning" text="dark" className="fs-6 px-3 py-2">
              🏅 {presences.length} / {coaches.length} coachs présents
            </Badge>
          </div>

          <Row className="g-4">
            <Col lg={8}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-0 py-3">
                  <h5 className="mb-0 fw-semibold">🔍 Rechercher un coach</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="p-4 border-bottom">
                    <InputGroup size="lg">
                      <InputGroup.Text className="bg-light border-end-0">🔍</InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Tapez le nom, prénom ou sport..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="border-start-0"
                      />
                    </InputGroup>
                  </div>

                  <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {filteredCoaches.length === 0 ? (
                      <div className="text-center py-5">
                        <div style={{ fontSize: '3rem' }}>🏅</div>
                        <h5 className="text-muted">Aucun coach trouvé</h5>
                        {searchTerm && <Button variant="outline-primary" onClick={() => handleSearch('')}>🔄 Réinitialiser</Button>}
                      </div>
                    ) : (
                      <ListGroup variant="flush">
                        {filteredCoaches.map(coach => (
                          <ListGroup.Item key={coach.id} className="px-4 py-3 border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <div className="rounded-circle p-3 me-3" style={{ background: '#fff3cd' }}>🏅</div>
                                <div>
                                  <h6 className="mb-1 fw-semibold">
                                    {coach.first_name} {coach.last_name}
                                    {isPresent(coach.id) && <Badge bg="success" className="ms-2">✅ Présent</Badge>}
                                  </h6>
                                  <div className="d-flex flex-wrap gap-1">
                                    {(coach.sports || []).map(s => <Badge key={s} bg="primary" className="px-2">{s}</Badge>)}
                                  </div>
                                </div>
                              </div>
                              <div>
                                {isPresent(coach.id) ? (
                                  <Badge bg="success" className="fs-6 px-3 py-2">✅ Présent</Badge>
                                ) : (
                                  <Button variant="warning" onClick={() => markPresence(coach.id)} size="lg" className="px-4">
                                    ✅ Signer
                                  </Button>
                                )}
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="shadow-sm border-0 sticky-top" style={{ top: '20px' }}>
                <Card.Header style={{ background: '#856404', color: 'white' }} className="py-3">
                  <h5 className="mb-0 fw-semibold">📊 Coachs présents</h5>
                </Card.Header>
                <Card.Body>
                  <div className="text-center mb-4">
                    <div className="display-4 fw-bold" style={{ color: '#856404' }}>{presences.length}</div>
                    <div className="text-muted">Coach(s) signé(s)</div>
                  </div>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Taux de présence</small>
                      <small>{coaches.length > 0 ? Math.round((presences.length / coaches.length) * 100) : 0}%</small>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div className="progress-bar" style={{ width: coaches.length > 0 ? `${(presences.length / coaches.length) * 100}%` : '0%', background: '#856404' }} />
                    </div>
                  </div>

                  {presences.length > 0 && (
                    <div className="mt-3">
                      <h6 className="text-muted mb-2">Coachs signés :</h6>
                      {presences.map(p => {
                        const coach = coaches.find(c => c.id === p.coach_id)
                        return coach ? (
                          <div key={p.id} className="d-flex align-items-center mb-2">
                            <span className="me-2">🏅</span>
                            <span className="fw-medium">{coach.first_name} {coach.last_name}</span>
                          </div>
                        ) : null
                      })}
                    </div>
                  )}

                  <div className="mt-4 pt-3 border-top">
                    <Button variant="outline-warning" className="w-100" onClick={() => navigate(`/checkin/coach-qr/${sessionId}`)} size="lg">
                      📷 Passer au scan QR
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </Layout>
    </ProtectedRoute>
  )
}

export default CheckinCoachName